import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Modal from 'tbk-components/src/components/Modal';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Tooltip from 'bootstrap/js/dist/tooltip';
import CareersInnerSidebarForm, {
	ICareersInnerSidebarFormProps,
} from './CareersInnerSidebarForm';
import {
	IDashboardCardI18n,
	IDashboardCareerCardProps,
} from '../ApplicantDashboard/DashboardCard';
import DashboardCardModal, {
	IDashboardCardModalI18n,
} from '../ApplicantDashboard/DashboardCardModal';

export interface IDashboardCareerCardPropsWithI18n
	extends IDashboardCareerCardProps {
	i18n: IDashboardCardModalI18n;
}

export interface ICareersInnerSidebarProps extends IBaseElementProps {
	/**
	 * Career Opportunity Code
	 */
	careerOpportunityCode?: string;
	/**
	 * Career Opportunity Title
	 */
	careerOpportunityTitle?: string;
	/**
	 * Career Opportunity Group
	 */
	careerOpportunityGroup?: string;
	/**
	 * Career Opportunity Tags
	 */
	careerOpportunityTags?: ILink[];
	/**
	 * Career Opportunity Apply Now Button Text
	 */
	applyNowButtonText?: string;
	/**
	 * Career Opportunity Full Apply Modal Heading
	 */
	fullApplyModalHeading?: string;
	/**
	 * Career Opportunity Full Apply Modal Description
	 */
	fullApplyModalDescription?: string;
	/**
	 * Career Opportunity Quick Apply Button Text
	 */
	quickApplyButtonText?: string;
	/**
	 * Career Opportunity Quick Apply Button Link ( bypasses modal )
	 */
	quickApplyButtonLink?: ILink;
	/**
	 * Career Opportunity Quick Apply Tooltip Text
	 */
	quickApplyTooltipText?: string;
	/**
	 * Content to show to the left of the Quick Apply form
	 */
	quickApplyModalContent?: string;
	/**
	 * Redirect to this URL after the form is submitted.
	 */
	submitAppRedirectUrl?: string;
	/**
	 * Career Opportunity Modal Logo
	 */
	modalLogo?: IImage;
	/**
	 * The tagline appears next to the logo when the form has been successfully submitted.
	 */
	logoTagline?: string;
	/**
	 * Career Opportunity Reminder Link Text
	 */
	reminderLinkText?: string;
	/**
	 * Career Opportunity Reminder Modal Heading
	 */
	remindersModalHeading?: string;
	/**
	 * Career Opportunity Reminder Modal Text
	 */
	remindersModalText?: string;
	/**
	 * Career Opportunity Reminder Modal Text Author
	 */
	remindersModalTextAuthor?: string;
	/**
	 * Career Opportunity Reminder Modal Form Heading
	 */
	reminderModalFormHeading?: string;
	/**
	 * Exists only to make storybooks a little easier to test.
	 */
	defaultIsConfirmed?: boolean;
	actionedCareerCard?: IDashboardCareerCardPropsWithI18n;
	refreshPageAfterSubmitModalClose?: boolean;
	children;
}

/**
 * Calian Inner Sidebar
 */
const CareersInnerSidebar: React.FC<ICareersInnerSidebarProps> = ({
	id,
	className,
	classNames = [],
	careerOpportunityCode,
	careerOpportunityTitle,
	careerOpportunityGroup,
	careerOpportunityTags,
	applyNowButtonText,
	fullApplyModalHeading,
	fullApplyModalDescription,
	quickApplyButtonText,
	quickApplyButtonLink,
	quickApplyTooltipText,
	quickApplyModalContent,
	submitAppRedirectUrl,
	modalLogo,
	logoTagline,
	reminderLinkText,
	remindersModalHeading,
	remindersModalText,
	remindersModalTextAuthor,
	reminderModalFormHeading,
	defaultIsConfirmed = false,
	actionedCareerCard,
	refreshPageAfterSubmitModalClose,
	children,
}) => {
	const [isConfirmed, setIsConfirmed] = useState(defaultIsConfirmed);
	const [showModal, setShowModal] = useState(null);
	const [hasClickedCreateAccount, setHasClickedCreateAccount] =
		useState(false);
	const ref = useRef(null);

	useEffect(() => {
		// @ts-ignore
		if (null === showModal || !window?.jQuery) {
			return;
		}

		setIsConfirmed(false);
		const onConfirmation = (event, formId) => {
			setIsConfirmed(true);
			if (submitAppRedirectUrl) {
				// Hiding modal triggers redirect below
				setShowModal(null);
			}
		};

		// @ts-ignore
		window.jQuery(document).on('gform_confirmation_loaded', onConfirmation);
		document.dispatchEvent(new CustomEvent('gform_main_scripts_loaded'));

		// See https://gitlab.tbkdev.com/tbk-devs/calian.com/-/issues/951
		const createAccount = document.querySelector(
			'.full-apply-consent-callout input[type="checkbox"]',
		);
		if (createAccount) {
			createAccount.addEventListener('change', (event) => {
				// @ts-ignore
				setHasClickedCreateAccount(event.target.checked);
			});
		} else {
			setHasClickedCreateAccount(false);
		}

		return () => {
			window
				// @ts-ignore
				.jQuery(document)
				.off('gform_confirmation_loaded', onConfirmation);
			setHasClickedCreateAccount(false);
		};
	}, [showModal]);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const tooltipTriggerList = ref.current.querySelectorAll(
			'[data-bs-toggle="tooltip"]',
		);
		const tooltipList = [...tooltipTriggerList].map(
			(tooltipTriggerEl) => new Tooltip(tooltipTriggerEl),
		);

		const innerSidebarTitle = ref.current.querySelector(
			'.calian-careers-inner-sidebar__title',
		);

		if (innerSidebarTitle) {
			if (innerSidebarTitle.textContent.length > 40) {
				innerSidebarTitle.classList.add('h2');
			}

			if (
				innerSidebarTitle.textContent.length > 14 &&
				innerSidebarTitle.textContent.indexOf(' ') === -1
			) {
				innerSidebarTitle.classList.add('h2');
			}

			// Checking if title is a continuous string of more than 12 characters with no spaces. If so, add a <br/> tag to the title if a / is present to prevent the title from overflowing the container and the words breaking in the middle of a word.

			if (
				innerSidebarTitle.textContent.length > 12 &&
				innerSidebarTitle.textContent.indexOf(' ') === -1
			) {
				const textContent = innerSidebarTitle.textContent;
				const newHTML = textContent.replace(/\//g, '/<br/>');
				innerSidebarTitle.innerHTML = newHTML;
			}
		}
	}, []);

	useEffect(() => {
		// Reload or redirect after submission
		if (isConfirmed && showModal === null) {
			if (submitAppRedirectUrl) {
				window.location.href = submitAppRedirectUrl;
				return;
			}
			if (refreshPageAfterSubmitModalClose) {
				window.location.reload();
			}
		}
	}, [showModal, isConfirmed, refreshPageAfterSubmitModalClose]);

	let modalContent = null;
	const registrationCTA =
		isConfirmed &&
		!hasClickedCreateAccount &&
		Array.isArray(children) &&
		children.length > 3 ? (
			<div className="col-12 col-md-6">{children[3]}</div>
		) : null;
	const hasRegistrationCTA = !!registrationCTA;

	const leftColumnClasses =
		'col-12 col-md-6 pt-4 pb-4 px-5 py-md-8 px-md-6 ps-md-8';
	const rightColumnClasses =
		'col-12 col-md-6 pt-0 pb-3 px-5 py-md-8 px-md-3 pe-md-8';
	const fullWidthClasses = 'col-12 py-8 px-3 px-lg-9';

	// Create switch statement to determine which modal to show
	switch (showModal) {
		case 0:
			// Full Apply
			modalContent = (
				<div className="row g-0 calian-careers-inner-sidebar__full-apply-modal">
					<div
						className={
							isConfirmed && hasRegistrationCTA
								? leftColumnClasses
								: fullWidthClasses
						}
					>
						{fullApplyModalHeading && !isConfirmed && (
							<h2 className="text-center">
								{fullApplyModalHeading}
							</h2>
						)}
						{fullApplyModalDescription && !isConfirmed && (
							<p className="calian-careers-inner-sidebar__modal--text text-center w-md-70 mx-auto mb-4">
								{fullApplyModalDescription}
							</p>
						)}
						<div className="calian-careers-inner-sidebar__modal--form">
							<CareersInnerSidebarForm
								isConfirmation={isConfirmed}
								logoTagline={logoTagline}
								children={children[showModal]}
							/>
						</div>
					</div>
					{registrationCTA}
				</div>
			);
			break;
		case 1:
			// Quick Apply
			modalContent = (
				<div className="row">
					<div
						className={cn(
							leftColumnClasses,
							'd-flex flex-column align-items-start',
							{ 'd-none': isConfirmed },
						)}
					>
						{quickApplyModalContent && (
							<div dangerouslySetInnerHTML={{ __html: quickApplyModalContent }} />
						)}
						<Image {...modalLogo} className="mt-auto img-fluid" />
					</div>
					<div
						className={
							isConfirmed
								? hasRegistrationCTA
									? leftColumnClasses
									: fullWidthClasses
								: rightColumnClasses
						}
					>
						<div className="calian-careers-inner-sidebar__modal--form">
							<CareersInnerSidebarForm
								isConfirmation={isConfirmed}
								logoTagline={logoTagline}
								children={children[showModal]}
							/>
						</div>
					</div>
					{registrationCTA}
				</div>
			);
			break;
		case 2:
			// Notification Reminder
			modalContent = (
				<div className="row">
					<div
						className={cn(
							leftColumnClasses,
							'd-flex flex-column align-items-start',
							{ 'd-none': isConfirmed },
						)}
					>
						{remindersModalHeading && (
							<h2>{remindersModalHeading}</h2>
						)}
						{remindersModalText && (
							<p
								className="calian-careers-inner-sidebar__modal--text mb-3 pe-lg-9"
								dangerouslySetInnerHTML={{
									__html: remindersModalText,
								}}
							/>
						)}
						{remindersModalTextAuthor && (
							<p className="calian-careers-inner-sidebar__modal--text small mb-3">
								{remindersModalTextAuthor}
							</p>
						)}
						<Image {...modalLogo} className="img-fluid mt-4" />
					</div>
					<div
						className={
							isConfirmed
								? hasRegistrationCTA
									? leftColumnClasses
									: fullWidthClasses
								: rightColumnClasses
						}
					>
						{reminderModalFormHeading && !isConfirmed && (
							<h3 className="mb-4 mb-md-6">
								{reminderModalFormHeading}
							</h3>
						)}
						<div className="calian-careers-inner-sidebar__modal-reminder-form-container">
							<CareersInnerSidebarForm
								isConfirmation={isConfirmed}
								logoTagline={logoTagline}
								children={children[showModal]}
							/>
						</div>
					</div>
					{registrationCTA}
				</div>
			);
			break;
		default:
			modalContent = null;
	}

	return (
		<div
			id={id}
			className={
				className || cn('calian-careers-inner-sidebar', ...classNames)
			}
			ref={ref}
		>
			<div className="calian-careers-inner-sidebar__container container-fluid">
				<div className="row">
					<div className="calian-careers-inner-sidebar__content col-12">
						<div className="calian-careers-inner-sidebar__content--main">
							{careerOpportunityCode && (
								<p className="calian-careers-inner-sidebar__code">
									{careerOpportunityCode}
								</p>
							)}
							{careerOpportunityTitle && (
								<h1 className="calian-careers-inner-sidebar__title">
									{careerOpportunityTitle}
								</h1>
							)}
							{careerOpportunityGroup && (
								<p className="calian-careers-inner-sidebar__group">
									{careerOpportunityGroup}
								</p>
							)}
							{careerOpportunityTags &&
								careerOpportunityTags.length > 0 && (
									<ul className="calian-careers-inner-sidebar__tags list-inline">
										{careerOpportunityTags.map(
											(
												{ label, href, target },
												index,
											) => (
												<li
													key={index}
													className="calian-careers-inner-sidebar__tag list-inline-item"
												>
													{href ? (
														<a
															href={href}
															target={target}
															className="calian-careers-inner-sidebar__tag--btn btn btn-sm btn-white"
														>
															{label}
														</a>
													) : (
														<span className="calian-careers-inner-sidebar__tag--btn btn btn-sm btn-white">
															{label}
														</span>
													)}
												</li>
											),
										)}
									</ul>
								)}
						</div>
						<div className="calian-careers-inner-sidebar__content--footer h-auto">
							{applyNowButtonText && (
								<div className="w-100 h-auto w-sm-auto d-flex flex-column justify-content-start flex-md-row justify-content-md-center align-items-md-center">
									<Button
										className="calian-careers-inner-sidebar__quick-apply-btn btn btn-primary"
										onClick={() => setShowModal(0)}
									>
										{applyNowButtonText}
									</Button>
									{quickApplyButtonText && (
										<Button
											className="calian-careers-inner-sidebar__quick-apply-btn btn d-flex flex-row justify-content-center align-items-center text-primary text-charcoal-hover ms-1 ps-2 pe-0"
											onClick={() => setShowModal(1)}
										>
											{quickApplyButtonText}
											{quickApplyTooltipText && (
												<i
													className="icon icon-question btn d-flex justify-content-center align-items-center text-primary text-charcoal-hover fs-5 ms-2 p-0"
													data-bs-toggle="tooltip"
													data-bs-custom-class="calian-careers-inner-sidebar__tooltip"
													data-bs-placement="top"
													data-bs-title={
														quickApplyTooltipText
													}
												></i>
											)}
										</Button>
									)}
								</div>
							)}
							{quickApplyButtonText && !applyNowButtonText && (
								<Button
									className="calian-careers-inner-sidebar__quick-apply-btn btn btn-primary"
									onClick={() => setShowModal(1)}
								>
									{quickApplyButtonText}
								</Button>
							)}
							{quickApplyButtonLink && (
								<Button
									className="calian-careers-inner-sidebar__quick-apply-btn btn btn-primary"
									{...quickApplyButtonLink}
								/>
							)}
							{reminderLinkText && (
								<a
									className="calian-careers-inner-sidebar__notification-link w-100 w-sm-auto link-primary fs-7 fs-md-6 fw-bold mt-3 ls-1"
									onClick={() => setShowModal(2)}
								>
									{reminderLinkText}
									<i className="icon icon-bell btn btn-white d-flex justify-content-center align-items-center fs-5 ms-3 p-0"></i>
								</a>
							)}
							{actionedCareerCard && (
								<a
									className="calian-careers-inner-sidebar__notification-link w-100 w-sm-auto link-primary fs-7 fs-md-6 fw-bold mt-3 ls-1"
									onClick={() =>
										setShowModal('dashboardCard')
									}
								>
									{actionedCareerCard.fullApplicationSubmitted
										? actionedCareerCard.i18n
												.fullApplicationSubmitted
										: actionedCareerCard.quickApplicationSubmitted
										? actionedCareerCard.i18n
												.quickApplicationSubmitted
										: actionedCareerCard.notificationFormSubmitted
										? actionedCareerCard.i18n
												.notificationFormSubmitted
										: actionedCareerCard.i18n
												.quickApplicationSubmitted}
									<i className="icon icon-success btn btn-white d-flex justify-content-center align-items-center fs-5 ms-3 p-0"></i>
								</a>
							)}
						</div>
					</div>
				</div>
			</div>
			{!isNaN(parseInt(showModal, 10)) && (
				<Modal
					onCancel={() => setShowModal(null)}
					// If hasRegistrationCTA is true, then add class to modal
					modalClassNames={[
						'calian-careers-inner-sidebar__modal modal-dialog modal-dialog-centered',
						hasRegistrationCTA && 'has-registration-cta',
					]}
					headerClassNames={[
						'modal-header',
						'position-absolute',
						'top-0',
						'start-0',
						'w-100',
					]}
					bodyClassNames={['modal-body', 'p-0']}
					trapFocus
				>
					<div className="container-fluid g-0">{modalContent}</div>
				</Modal>
			)}
			{'dashboardCard' === showModal && (
				<DashboardCardModal
					card={actionedCareerCard}
					i18n={actionedCareerCard.i18n}
					onCancel={() => setShowModal(null)}
				/>
			)}
		</div>
	);
};

export default CareersInnerSidebar;
